import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { PurchaseOrderStatus } from "@/server/platform/db/schema/purchase-orders";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { Button } from "@/components/ui/button";
import { Loader } from "@/shared/ui/Loader";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { ClipboardListIcon, PlusIcon } from "lucide-react";
import { usePurchaseOrdersQuery } from "../api/purchase-orders-queries";
import {
  PurchaseOrdersFilter,
  type PurchaseOrdersSearch,
} from "../ui/purchase/PurchaseOrdersFilter";
import { PurchaseOrdersTable } from "../ui/purchase/PurchaseOrdersTable";
import { PurchaseOrderOpenSummaryStrip } from "../ui/purchase/PurchaseOrderOpenSummaryStrip";
import { PO_MENU_TITLE } from "../ui/purchase/purchaseLaoLabels";

export function PurchaseOrdersPage() {
  const nav = useNavigate({ from: "/app/inventory/purchase-orders" });
  const search = useSearch({
    from: "/app/inventory/purchase-orders",
  }) as PurchaseOrdersSearch;

  const params = {
    status: search.status as PurchaseOrderStatus | undefined,
    supplierId: search.supplierId,
    orderDateFrom: search.orderDateFrom,
    orderDateTo: search.orderDateTo,
    poNo: search.poNo,
    limit: search.limit ?? 20,
    offset: search.offset ?? 0,
  };

  const { data, isLoading } = usePurchaseOrdersQuery(params);
  const canCreate = useActionPermission(["inventory:purchase:create"]);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: PO_MENU_TITLE, to: "/app/inventory/purchase-orders" },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="flex items-center gap-2 font-bold text-2xl tracking-tight">
              <ClipboardListIcon className="size-5" />
              {PO_MENU_TITLE}
            </h2>
            <p className="text-muted-foreground">
              ສັ່ງຊື້ທີ່ Hub · ອະນຸມັດ · ຮັບເຂົ້າ lot
            </p>
          </div>
          {canCreate && (
            <Button
              onClick={() => nav({ to: "/app/inventory/purchase-orders/create" })}
            >
              <PlusIcon className="size-4" />
              ສ້າງ PO
            </Button>
          )}
        </div>

        <PurchaseOrderOpenSummaryStrip />

        <div className="overflow-hidden rounded-xl border bg-card">
          <PurchaseOrdersFilter />
          {isLoading ? (
            <div className="flex justify-center py-16">
              <Loader />
            </div>
          ) : (
            <PurchaseOrdersTable
              data={items}
              offset={params.offset}
              limit={params.limit}
              totalCount={total}
              onPaginationChange={(o, l) =>
                nav({ search: { ...search, offset: o, limit: l } })
              }
            />
          )}
        </div>
      </Main>
    </>
  );
}
